// Integration Store
// Zustand store for managing providers and connections state

import { create } from 'zustand';
import type { ProviderResponse, UserConnection, ProviderCategory } from './types';
import * as api from './api';

interface IntegrationState {
  // Providers
  providers: ProviderResponse[];
  providersLoading: boolean;
  providersError: string | null;
  selectedProvider: ProviderResponse | null;
  providerFilter: ProviderCategory;
  providerSearch: string;

  // Connections
  connections: UserConnection[];
  connectionsLoading: boolean;
  connectionsError: string | null;

  // OAuth flow state
  oauthInProgress: boolean;
  oauthProvider: string | null;
  oauthError: string | null;

  // Actions
  fetchProviders: (search?: string) => Promise<void>;
  fetchProvider: (provider: string) => Promise<ProviderResponse>;
  setProviderFilter: (category: ProviderCategory) => void;
  setProviderSearch: (search: string) => void;
  setSelectedProvider: (provider: ProviderResponse | null) => void;

  fetchConnections: () => Promise<void>;
  addConnection: (connection: UserConnection) => void;
  removeConnection: (alias: string) => Promise<void>;

  startOAuth: (provider: string) => void;
  completeOAuth: (connection: UserConnection) => void;
  failOAuth: (error: string) => void;
  resetOAuth: () => void;
}

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  // Initial state
  providers: [],
  providersLoading: false,
  providersError: null,
  selectedProvider: null,
  providerFilter: 'All',
  providerSearch: '',

  connections: [],
  connectionsLoading: false,
  connectionsError: null,

  oauthInProgress: false,
  oauthProvider: null,
  oauthError: null,

  // Provider actions
  fetchProviders: async (search?: string) => {
    set({ providersLoading: true, providersError: null });
    try {
      const providers = await api.getProviders(search);
      set({ providers, providersLoading: false });
    } catch (error) {
      set({
        providersError: (error as Error).message,
        providersLoading: false,
      });
    }
  },

  fetchProvider: async (provider: string) => {
    try {
      const providerData = await api.getProvider(provider);
      set({ selectedProvider: providerData });
      return providerData;
    } catch (error) {
      throw error;
    }
  },

  setProviderFilter: (category: ProviderCategory) => {
    set({ providerFilter: category });
  },

  setProviderSearch: (search: string) => {
    set({ providerSearch: search });
  },

  setSelectedProvider: (provider: ProviderResponse | null) => {
    set({ selectedProvider: provider });
  },

  // Connection actions
  fetchConnections: async () => {
    set({ connectionsLoading: true, connectionsError: null });
    try {
      const connections = await api.getUserConnections();
      set({ connections, connectionsLoading: false });
    } catch (error) {
      set({
        connectionsError: (error as Error).message,
        connectionsLoading: false,
      });
    }
  },

  addConnection: (connection: UserConnection) => {
    set((state) => ({
      connections: [connection, ...state.connections],
    }));
  },

  removeConnection: async (alias: string) => {
    try {
      await api.deleteConnection(alias);
      set((state) => ({
        connections: state.connections.filter((c) => c.alias !== alias),
      }));
    } catch (error) {
      throw error;
    }
  },

  // OAuth flow actions
  startOAuth: (provider: string) => {
    set({
      oauthInProgress: true,
      oauthProvider: provider,
      oauthError: null,
    });
  },

  completeOAuth: (connection: UserConnection) => {
    set((state) => ({
      oauthInProgress: false,
      oauthProvider: null,
      oauthError: null,
      connections: [connection, ...state.connections],
    }));
  },

  failOAuth: (error: string) => {
    set({
      oauthInProgress: false,
      oauthError: error,
    });
  },

  resetOAuth: () => {
    set({
      oauthInProgress: false,
      oauthProvider: null,
      oauthError: null,
    });
  },
}));

// Selectors
export const useProviders = () => useIntegrationStore((state) => state.providers);
export const useConnections = () => useIntegrationStore((state) => state.connections);
export const useSelectedProvider = () => useIntegrationStore((state) => state.selectedProvider);
export const useOAuthState = () =>
  useIntegrationStore((state) => ({
    inProgress: state.oauthInProgress,
    provider: state.oauthProvider,
    error: state.oauthError,
  }));

// Filtered providers selector
export const useFilteredProviders = () =>
  useIntegrationStore((state) => {
    let filtered = state.providers;

    // Filter by category (categories is an array)
    if (state.providerFilter !== 'All') {
      filtered = filtered.filter((p) =>
        p.categories?.some(c => c.toLowerCase() === state.providerFilter.toLowerCase())
      );
    }

    // Filter by search
    if (state.providerSearch) {
      const search = state.providerSearch.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.provider.toLowerCase().includes(search) ||
          (p.display_name?.toLowerCase() || '').includes(search)
      );
    }

    return filtered;
  });
